import classNames from 'classnames';
import {getMediaSrc} from 'core/helpers/media';
import {IMedia} from 'modules/products/types';
import React, {FC, useCallback, useId, useMemo, useRef, useState} from 'react';
import style from './style.module.scss';
import Picture from 'components/Picture';
import {makeSources} from 'modules/products/helpers';
import {Slider} from 'modules/slider/components/Slider';
import {OnlyMobile} from 'components/OnlyMobile';
import {useIsMobileByScreen} from 'core/hooks/useIsMobileByScreen';

interface IProps {
    className?: string;
    imageList: IMedia['images'];
    isDotPagination?: boolean;
}

const SLIDES_NUMBER: Record<string, number> = {
    THREE: 3,
};

export const ProductImageSwitcher: FC<IProps> = ({className, imageList, isDotPagination}) => {
    const isMobile = useIsMobileByScreen();

    const shortImagesList = useMemo(() => imageList?.slice(0, SLIDES_NUMBER.THREE), [imageList]);
    const initialActiveItem = isMobile ? `${shortImagesList[0]}0` : '';
    const [activeItemId, setActiveItemId] = useState<string>(`${shortImagesList[0]}0`);
    const sliderRef = useRef<HTMLDivElement>(null);

    const uid = useId();

    const handelBulletHover = useCallback((id: string) => {
        return () => {
            setActiveItemId(id.toString());
        };
    }, []);

    return (
        <div
            className={classNames(className, style.productImageSwitcher, isDotPagination ? style.isDotPagination : '')}
        >
            {shortImagesList && (
                <>
                    <OnlyMobile displayType={'contents'}>
                        <Slider
                            className={style.slider}
                            isNavigation={false}
                            isPagination={true}
                            loop={true}
                            slidesPerGroup={1}
                            slidesPerView={'auto'}
                            spaceBetween={0}
                        >
                            {shortImagesList.map((image, index) => {
                                if (!image) {
                                    return null;
                                }
                                return (
                                    <div
                                        data-skeleton-item={true}
                                        className={classNames(style.productImageSwitcherImageSlide, {
                                            [style.productImageSwitcherImageSlideActive]:
                                                activeItemId === `${image}${index}`,
                                        })}
                                        key={`${image}-${index}`}
                                    >
                                        <Picture
                                            key={`${uid}${image}${index}`}
                                            sources={makeSources(image)}
                                            src={getMediaSrc(image, {
                                                height: 172,
                                                isWebp: false,
                                            })}
                                            alt={'Изображение товара'}
                                            mimeTypes={{
                                                xxl: 'image/webp',
                                                xl: 'image/webp',
                                                lg: 'image/webp',
                                                sm: 'image/webp',
                                                // xs: 'image/webp',
                                            }}
                                        />
                                    </div>
                                );
                            })}
                        </Slider>
                    </OnlyMobile>
                    <div className={style.productImageSwitcherImageContainer} ref={sliderRef}>
                        {shortImagesList.map((image, index) => {
                            if (!image) {
                                return null;
                            }
                            return (
                                <div
                                    className={classNames(style.productImageSwitcherImageSlide, {
                                        [style.productImageSwitcherImageSlideActive]:
                                            activeItemId === `${image}${index}`,
                                    })}
                                    key={`${image}-${index}`}
                                >
                                    <Picture
                                        key={`${uid}${image}${index}`}
                                        sources={makeSources(image)}
                                        src={getMediaSrc(image, {
                                            height: 200,
                                            isWebp: true,
                                        })}
                                        alt={'Изображение товара'}
                                    />
                                </div>
                            );
                        })}
                    </div>
                    <div className={style.productImageSwitcherPaginationContainer}>
                        {shortImagesList.map((image, index) => {
                            if (!image) {
                                return null;
                            }
                            return (
                                <div
                                    className={style.productImageSwitcherPagination}
                                    key={`${image}${index}`}
                                    onMouseEnter={handelBulletHover(`${image}${index}`)}
                                    onMouseLeave={() => setActiveItemId(initialActiveItem)}
                                />
                            );
                        })}
                    </div>
                </>
            )}

            {shortImagesList && 1 < shortImagesList.length && (
                <ul className={classNames(style.productImageSwitcherNavItems, 'product-image-switcher__nav')}>
                    {shortImagesList.map((image, index) => {
                        if (!image) {
                            return null;
                        }

                        return (
                            <li
                                className={classNames(style.productImageSwitcherNavItem, {
                                    [style.productImageSwitcherNavItemActive]: activeItemId === `${image}${index}`,
                                })}
                                key={image + index}
                                onMouseEnter={handelBulletHover(`${image}${index}`)}
                            />
                        );
                    })}
                </ul>
            )}
        </div>
    );
};
